/**
 * @module ui.store-summary
 * @version 4.5.2
 * @description LIFTR Summary Web Component - Aggregates ALL stores via registry
 * 
 * Features:
 *   - Prefill: Finds all store roots and registers them
 *   - Aggregation: Earliest/Latest opening times, Counts
 *   - Output: Aggregated status + Total count
 * 
 * @element liftr-store-summary
 * 
 * @fires liftr:store-summary:updated - When aggregated status changes
 * 
 * @attr {string} liftr-id - Optional identifier
 * @attr {string} liftr-state - Unified state output:
 *   Summary: open, closed, closing, opening, unknown
 *   Other: active
 * @attr {string} liftr-summary-count - Output: Total number of stores in registry
 * 
 * @css-selectors
 * liftr-store-summary[liftr-state~="open"] { }
 * liftr-store-summary[liftr-state~="closed"] { }
 * liftr-store-summary[liftr-state~="closing"] { }
 * liftr-store-summary[liftr-state~="opening"] { }
 * liftr-store-summary[liftr-summary-count="0"] { }
 * 
 * @example
 * <liftr-store-summary liftr-id="store-summary">
 *   <span liftr-role="live-label-current"></span>
 *   <span liftr-role="live-label-next"></span>
 * </liftr-store-summary>
 * 
 * @changelog
 * - 4.5.2: FIX – formatTime normalisiert Span-Enden > 1440 (Über-Mitternacht,
 *          z. B. 28:00 → 04:00). Die Aggregation bleibt auf Rohwerten.
 * - 4.5.1: CLEANUP - Dropped the dead store-side holiday override. No longer
 *          reads schedule.holiday from the store-data payload (the field is
 *          removed store-side — Google opening_hours is the sole source for
 *          hours and holidays) and no longer builds holidayBySlug/holidaySlugByISO
 *          nor passes them to createOpeningCore. Removed the now-unused
 *          normalizeHolidayOverrideMap/buildHolidaySlugMap imports. Pure cleanup,
 *          no behavior change. The market-global legacy path in the core is unaffected.
 * - 4.5.0: AUDIT COMPLIANCE - Quality Checks v4.8
 *        - BREAKING: static NAME changed from 'summary' to 'store-summary'
 *          → Events change: liftr:summary:* → liftr:store-summary:*
 *          → Update any external listeners accordingly
 *        - Added ROLES, STATES, EVENTS, SETTINGS constants (frozen)
 *        - All string literals → constants
 *        - Renamed ROLES import from api.live.js to LIVE_ROLES (avoid collision)
 *        - Correct named export pattern
 * - 4.0.0: Initial v4.0 implementation
 */

import { LiftrModule } from 'core.module';
import { i18n, settings, ATTRIBUTES } from 'core.liftr';
import { throttle, ensureLive, on } from 'core.utilities';
import {
  STATUS,
  ROLES as LIVE_ROLES,
  OUTPUT,
  STATE_TOKENS,
  DEFAULTS,
  createDateFx,
  createOpeningCore,
  getClosingThreshold,
  transformLiveLabels,
  registerStore,
  snapshotAll,
  subscribeStores
} from 'api.live';

/* ═══════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * Note: Uses shared live-label-* roles (shared with ui.service).
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  LABEL_CURRENT: 'live-label-current',
  LABEL_NEXT:    'live-label-next'
});

/**
 * State tokens for liftr-state attribute.
 * Summary states are prefixed with "summary-" (exclusive, one at a time).
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  OPEN:    'open',
  CLOSED:  'closed',
  CLOSING: 'closing',
  OPENING: 'opening',
  UNKNOWN: 'unknown'
});

/** All summary state values for bulk removal (derived locally for safety) */
const BULK_STATES = Object.values(STATES);

/**
 * Event action names (auto-prefixed with liftr:store-summary:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  UPDATED: 'updated'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  // Reserved for future use
});

/* ═══════════════════════════════════════════════════════════════════
 * HELPER FUNCTIONS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Reads schedule from store root element (json-source)
 * Note: Cross-component query — reads data from liftr-store elements
 * @param {Element} storeRoot - Store root element
 * @returns {{ handle: string, regular: object }|null}
 */
function readScheduleForRoot(storeRoot) {
  // Uses LIVE_ROLES.jsonSource from api.live.js (cross-component role)
  const script = storeRoot.querySelector(
    `script[type="application/json"][${ATTRIBUTES.ROLE}="${LIVE_ROLES.jsonSource}"]`
  );
  if (!script) return null;

  try {
    const raw = JSON.parse(script.textContent || 'null');
    const handle = raw?.handle;
    const regular = raw?.schedule?.regular;

    if (!handle || !regular) return null;

    return { handle: String(handle), regular };
  } catch {
    return null;
  }
}

/**
 * Formats time string
 * @param {number} mins - Minutes since midnight of the opening day.
 *   Values >= 1440 (overnight spans) are normalized into the day's range.
 * @param {object} dateFx - DateFx object
 * @returns {string} Formatted time (HH:MM)
 */
function formatTime(mins, dateFx) {
  return dateFx.fmtTime(((mins % 1440) + 1440) % 1440);
}

/**
 * Formats day string (today/tomorrow/weekday)
 * @param {number} step - Day offset (0=today, 1=tomorrow, ...)
 * @param {object} dateFx - DateFx object
 * @param {object} labels - Labels object
 * @returns {string} Formatted day
 */
function formatDay(step, dateFx, labels) {
  const n = step | 0;
  if (n === 0) return labels.days.today;
  if (n === 1) return labels.days.tomorrow;
  
  const base = dateFx.nowInfo().date;
  const wdIdx = dateFx.wkIdx(dateFx.addDays(base, n));
  return labels.week.long[wdIdx] || labels.week.long[0];
}

/**
 * Appends count to message (if > 0)
 * @param {string} msg - Base message
 * @param {number} count - Count
 * @returns {string} Message with count
 */
function appendCount(msg, count) {
  return (count && count > 0) ? `${msg} (${count})` : msg;
}

/**
 * Maps aggregated status to summary state token
 * @param {string} status - Aggregated status (open, closed, closing, opening, unknown)
 * @returns {string} Summary state token (open, closed, etc.)
 */
function toSummaryState(status) {
  return `${status}`;
}

/* ═══════════════════════════════════════════════════════════════════
 * LIFTR SUMMARY WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

class LiftrStoreSummary extends LiftrModule {
  
  /* ═══════════════════════════════════════════════════════════════════
   * STATIC PROPERTIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  static NAME = 'store-summary';
  
  static DEFAULTS = {
    storesSelector: 'liftr-store',
    closingThresholdMin: DEFAULTS.closingThresholdMin
  };
  
  /* ═══════════════════════════════════════════════════════════════════
   * PRIVATE FIELDS
   * ═══════════════════════════════════════════════════════════════════ */
  
  /** @type {object|null} DateFx instance */
  #dateFx = null;
  
  /** @type {object|null} Transformed labels */
  #labels = null;
  
  /** @type {Element|null} Current label element */
  #elCurrent = null;
  
  /** @type {Element|null} Next label element */
  #elNext = null;
  
  /** @type {boolean} Whether stores have been prefilled */
  #prefilled = false;
  
  /** @type {object} Last rendered state for diffing */
  #lastState = { summaryState: null, current: null, next: null, total: null };
  
  /* ═══════════════════════════════════════════════════════════════════
   * LIFECYCLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  mount() {
    // Get labels from i18n system
    const rawLabels = i18n.get('live');
    if (!rawLabels) {
      throw new Error('[store-summary] i18n.live translations missing');
    }
    this.#labels = transformLiveLabels(rawLabels);
    
    // Get market from settings
    const market = settings.market;
    if (!market?.timezone) {
      throw new Error('[store-summary] settings.market.timezone missing');
    }
    
    // Create DateFx instance
    this.#dateFx = createDateFx({ timezone: market.timezone });
    
    // Find label elements
    this.#elCurrent = this.role(ROLES.LABEL_CURRENT);
    this.#elNext = this.role(ROLES.LABEL_NEXT);
    
    // Setup ARIA live regions
    if (this.#elCurrent) ensureLive(this.#elCurrent, 'status');
    if (this.#elNext) ensureLive(this.#elNext, 'status');
    
    // Prefill stores from DOM
    this.#prefillStoresOnce();
    
    // Subscribe to store updates (throttled)
    const onStoreUpdate = throttle(() => this.#recalc(), 32, { leading: true, trailing: true });
    const unsubscribe = subscribeStores(onStoreUpdate);
    this.addDisposable(unsubscribe);
    this.addDisposable(() => onStoreUpdate.cancel?.());
    
    // Initial calculation
    this.#recalc();
    
    // Handle pageshow (bfcache)
    const handlePageShow = (e) => {
      if (e && e.persisted) this.#recalc();
    };
    window.addEventListener('pageshow', handlePageShow);
    this.addDisposable(() => window.removeEventListener('pageshow', handlePageShow));
  }
  
  unmount() {
    // Remove all summary states
    this.state.remove(...BULK_STATES);
    
    // Clear count attribute (numeric, stays as attribute for CSS :has())
    this.removeAttribute(OUTPUT.summaryCount);
    
    // Clear label content
    if (this.#elCurrent) this.#elCurrent.textContent = '';
    if (this.#elNext) this.#elNext.textContent = '';
    
    // Reset state
    this.#prefilled = false;
    this.#lastState = { summaryState: null, current: null, next: null, total: null };
    
    super.unmount();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * PREFILL STORES
   * ═══════════════════════════════════════════════════════════════════ */
  
  #prefillStoresOnce() {
    if (this.#prefilled) return;
    this.#prefilled = true;
    
    const { storesSelector, closingThresholdMin } = this.config;

    // Cross-component: queries all liftr-store elements in DOM
    const allStoreRoots = [...document.querySelectorAll(storesSelector)];
    const existing = new Set(snapshotAll().map(x => String(x.id)));
    
    for (const storeRoot of allStoreRoots) {
      const sched = readScheduleForRoot(storeRoot);
      if (!sched) continue;
      
      const id = String(sched.handle);
      if (existing.has(id)) continue;

      // Create opening core engine. Stores draw hours and holidays solely from
      // Google (schedule.regular composite) — no store-side holiday override.
      const engine = createOpeningCore({
        dateFx: this.#dateFx,
        regular: sched.regular
      });
      
      // Create compute function for registry
      const compute = (now) => {
        const threshold = getClosingThreshold(closingThresholdMin);
        const status = engine.currentStatus(new Date(now), threshold);
        return {
          id,
          status: status.status || STATUS.UNKNOWN,
          closesMin: status.closesMin ?? null,
          nextMin: status.nextMin ?? null,
          nextStep: status.nextStep ?? 0,
          nextDelta: status.nextDelta ?? null
        };
      };
      
      // Register store (cleanup handled by store component itself)
      registerStore({ id, compute });
      existing.add(id);
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * AGGREGATION
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Sets summary state (exclusive - only one at a time)
   * @param {string} status - open, closed, closing, opening, unknown
   */
  #setSummaryState(status) {
    this.state.remove(...BULK_STATES);
    this.state.add(toSummaryState(status));
  }
  
  #recalc() {
    const stores = snapshotAll();
    const total = stores.length;
    const { closingThresholdMin } = this.config;
    
    // Handle empty state
    if (total === 0) {
      this.#updateOutput(STATUS.UNKNOWN, '', '', 0);
      return;
    }
    
    // Aggregation counters
    let open = 0, closing = 0, opening = 0;
    
    // Latest close time (for OPEN status)
    let latestCloseMin = null, latestCloseCount = 0;
    
    // Earliest close time (for CLOSING status)
    let earliestClosingCloseMin = null, earliestClosingCount = 0;
    
    // Earliest open time (for CLOSED status)
    let earliestOpenMin = null, earliestOpenStep = null, earliestOpenCount = 0;
    
    // Earliest opening time (for OPENING status)
    let earliestOpeningLeft = null, earliestOpeningCount = 0;
    
    const nowMin = this.#dateFx.nowInfo().mins;
    
    // Aggregate all stores
    for (const rec of stores) {
      const st = rec.status;
      
      // Count by status
      if (st === STATUS.OPEN) open++;
      else if (st === STATUS.CLOSING) closing++;
      else if (st === STATUS.OPENING) opening++;
      
      // Track close times for OPEN/CLOSING stores
      if (st === STATUS.OPEN || st === STATUS.CLOSING) {
        const cm = rec.closesMin;
        if (cm != null) {
          // Latest close (for "open" message)
          if (latestCloseMin == null || cm > latestCloseMin) {
            latestCloseMin = cm;
            latestCloseCount = 1;
          } else if (cm === latestCloseMin) {
            latestCloseCount++;
          }
          
          // Earliest close among CLOSING stores
          if (st === STATUS.CLOSING) {
            if (earliestClosingCloseMin == null || cm < earliestClosingCloseMin) {
              earliestClosingCloseMin = cm;
              earliestClosingCount = 1;
            } else if (cm === earliestClosingCloseMin) {
              earliestClosingCount++;
            }
          }
        }
      }
      
      // Track open times for CLOSED/OPENING stores
      if (st === STATUS.CLOSED || st === STATUS.OPENING) {
        const nm = rec.nextMin;
        const stp = rec.nextStep | 0;
        
        if (nm != null) {
          // Earliest open (for "closed" message)
          if (earliestOpenMin == null || stp < earliestOpenStep || (stp === earliestOpenStep && nm < earliestOpenMin)) {
            earliestOpenMin = nm;
            earliestOpenStep = stp;
            earliestOpenCount = 1;
          } else if (stp === earliestOpenStep && nm === earliestOpenMin) {
            earliestOpenCount++;
          }
        }
        
        // Earliest opening (for "opening" message)
        if (st === STATUS.OPENING) {
          const dl = rec.nextDelta;
          if (dl != null) {
            if (earliestOpeningLeft == null || dl < earliestOpeningLeft) {
              earliestOpeningLeft = dl;
              earliestOpeningCount = 1;
            } else if (dl === earliestOpeningLeft) {
              earliestOpeningCount++;
            }
          }
        }
      }
    }
    
    // Calculate aggregated status
    const openNow = open + closing;
    const allClosing = (closing > 0 && open === 0);
    
    let aggStatus = STATUS.UNKNOWN;
    let currentText = '';
    let nextText = '';
    const labels = this.#labels;
    
    if (openNow > 0) {
      if (allClosing) {
        // All open stores are closing soon
        aggStatus = STATUS.CLOSING;
        currentText = appendCount(labels.current.open_now({ count: openNow }), openNow);
        
        if (earliestClosingCloseMin != null) {
          const left = Math.max(0, earliestClosingCloseMin - nowMin);
          nextText = appendCount(labels.next.closes_in({ min: left }), earliestClosingCount);
        }
      } else {
        // Some stores are fully open
        aggStatus = STATUS.OPEN;
        currentText = appendCount(labels.current.open_now({ count: openNow }), openNow);
        
        if (latestCloseMin != null) {
          nextText = appendCount(
            labels.next.closes_today_at({ time: formatTime(latestCloseMin, this.#dateFx) }),
            latestCloseCount
          );
        }
      }
    } else if (opening > 0) {
      // Some stores are about to open
      aggStatus = STATUS.OPENING;
      currentText = appendCount(labels.current.closed, total);
      
      if (earliestOpeningLeft != null) {
        nextText = appendCount(labels.next.opens_in({ min: earliestOpeningLeft }), earliestOpeningCount);
      }
    } else {
      // All stores are closed
      aggStatus = STATUS.CLOSED;
      currentText = appendCount(labels.current.closed, total);
      
      if (earliestOpenMin != null) {
        const day = formatDay(earliestOpenStep | 0, this.#dateFx, labels);
        const time = formatTime(earliestOpenMin, this.#dateFx);
        nextText = appendCount(labels.next.opens_day_at({ day, time }), earliestOpenCount);
      }
    }
    
    this.#updateOutput(aggStatus, currentText, nextText, total);
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * OUTPUT
   * ═══════════════════════════════════════════════════════════════════ */
  
  #updateOutput(status, currentText, nextText, total) {
    const last = this.#lastState;
    let changed = false;
    
    const summaryState = toSummaryState(status);
    
    // Update summary state (exclusive)
    if (last.summaryState !== summaryState) {
      this.#setSummaryState(status);
      last.summaryState = summaryState;
      changed = true;
    }
    
    // Update count attribute (numeric value, stays as attribute for CSS :has())
    if (last.total !== total) {
      this.setAttribute(OUTPUT.summaryCount, String(total));
      last.total = total;
      changed = true;
    }
    
    // Update current label
    if (this.#elCurrent && last.current !== currentText) {
      this.#elCurrent.textContent = currentText;
      last.current = currentText;
      changed = true;
    }
    
    // Update next label
    if (this.#elNext && last.next !== nextText) {
      this.#elNext.textContent = nextText;
      last.next = nextText;
      changed = true;
    }
    
    // Emit event if status changed
    if (changed) {
      this.emit(EVENTS.UPDATED, {
        status,
        summaryState,
        total,
        current: currentText,
        next: nextText
      });
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

customElements.define('liftr-store-summary', LiftrStoreSummary);

export { LiftrStoreSummary, ROLES, STATES, EVENTS, SETTINGS };